import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { request } from '../../Utils/request';

// Import components
import { TripData } from './TripCard';
import ExploreTab from './TripTab/ExploreTab';
import TripsTab from './TripTab/TripsTab';
import BookingsTab from './TripTab/BookingsTab';
import TransactionsTab from './TripTab/TransactionsTab';
import ReservationsTab from './TripTab/ReservationsTab';
import ShareModal from '../../Components/ShareModal';
import { CenteredFab } from '../../Components/Fab/CustomButton';
import COLORS from '../../Utils/colors';
import { useTabNavigation } from '../../hooks/useTabNavigation';
import OptimizedTabPanel from '../../Components/OptimizedTabPanel';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

export interface IFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
  fileURL: string;
}

export interface ITask {
  placeRefId: string;
  name: string;
  file: IFile;
  description: string;
  _id: string;
}

export interface IDailySchedule {
  day: string;
  tasks: ITask[];
  _id: string;
}

export interface ISchedule {
  dailySchedule: IDailySchedule[];
}

export interface IBatch {
  from: string; // ISO string
  to: string;   // ISO string
  groupSize: number;
  maxGroupSizeAllowed: number;
  _id: string;
}

export interface ILocation {
  lat: number;
  lng: number;
  name: string;
}

export interface IUser {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
}

export interface ITrip {
  _id: string;
  title: string;
  pricePerPerson: number;
  batches: IBatch[];
  files: IFile[];
  schedule: ISchedule;
  notes: string;
  category: string[];
  location: ILocation;
  userId: IUser;
  displayTripId: string;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trip-tabpanel-${index}`}
      aria-labelledby={`trip-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const Trips: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.user.user);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedTripForShare, setSelectedTripForShare] = useState<TripData | null>(null);

  // Use custom tab navigation hook
  const { activeTabIndex, handleTabChange, tabs } = useTabNavigation();
  const isLoggedIn = !!currentUser;

  // Clear hash when component unmounts to reset tab on next visit
  useEffect(() => {
    return () => {
      // Clear hash when leaving the Trips page
      if (window.location.pathname !== '/trips') {
        window.location.hash = '';
      }
    };
  }, []);

  // Handle back button press - navigate to dashboard and reset tabs
  const handleBackClick = () => {
    // Clear the hash to reset tab state
    window.location.hash = '';
    // Navigate to dashboard
    navigate('/');
  };

  const handleTripEdit = async (trip: TripData) => {
    try {
      // Fetch full trip data
      const tripType = trip.type?.toLowerCase() === 'itinerary' ? 'itinerary' : 'trip';
      const endpoint = tripType === 'itinerary' ? '/itineraries' : '/trips';
      
      const response = await request<{ success: boolean; data: ITrip }>(
        `${endpoint}/${trip.id}`,
        {
          method: 'GET',
          authorization: true,
        }
      );

      if (response.success && response.data) {
        // Determine the capitalized type for the form
        const tripTypeLower = trip.type?.toLowerCase();
        const formTripType = tripTypeLower === 'itinerary' ? 'Itinerary' : 'Trip';
        
        // Navigate to createTrip screen with trip data for editing
        navigate('/createtrip', {
          state: {
            trip: response.data,
            isEdit: true,
            tripType: formTripType, // Pass capitalized version for form
          }
        });
      }
    } catch (error) {
      console.error('Error fetching trip for edit:', error);
      alert('Failed to load trip data for editing');
    }
  };

  const handleTripShare = (trip: TripData) => {
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin.replace('https://', 'http://');
    const shareUrl = `${baseUrl}/trips/${trip.id}`;
    
    setSelectedTripForShare({
      ...trip,
      shareUrl
    });
    setShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
    setSelectedTripForShare(null);
  };

  const handleTripClick = (trip: any) => {
    if (!trip) return;

    // If we're in the Bookings tab (index 2), navigate to booking details with trip data
    if (activeTabIndex === 2) {
      navigate(`/trips/${trip._id || trip.id}/bookings`, {
        state: { tripData: trip }
      });
    } else {
      // For other tabs, navigate to trip detail
      const tripType = trip.type || 'trip';
      navigate(`/trips/${trip._id || trip.id}?type=${tripType}`);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#fff'}}>
    {/* TODO:Remove by the client */}
      {/* <TopNavBar title="Trips and Itinerary" canGoBack onBackClick={handleBackClick} /> */}

      {/* Tabs - show only Explore when not logged in, all tabs when logged in */}
      <Box sx={{ borderBottom: '1px solid #E0E0E0', }}>
        <Tabs
          value={isLoggedIn ? activeTabIndex : 0}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              color: '#49454F',
            },
            '& .Mui-selected': {
              color: '#F57C00',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#F57C00',
            },
          }}
        >
          {(isLoggedIn ? tabs : tabs.slice(0, 1)).map((tab) => (
            <Tab key={tab.key} label={tab.label} sx={{
              fontSize: '12px', 
              color: '#F57C00',
              '&.Mui-selected': {
                color: '#FF5722',
                fontWeight: 'bold'
              }
            }} />
          ))}
        </Tabs>
      </Box>

      <Container maxWidth="md" sx={{ py: 2 }}>
        {/* Explore Tab - always visible (only tab when not logged in) */}
        <OptimizedTabPanel value={isLoggedIn ? activeTabIndex : 0} index={0}>
          <ExploreTab
            onTripClick={handleTripClick}
          />
        </OptimizedTabPanel>

        {/* Other tabs - only when logged in */}
        {isLoggedIn && (
          <>
            {/* Trips Tab */}
            <OptimizedTabPanel value={activeTabIndex} index={1}>
              <>
                <TripsTab
                  onTripEdit={handleTripEdit}
                  onTripShare={handleTripShare}
                  onTripClick={handleTripClick}
                />
                {/* FAB icon for create trip */}
                <CenteredFab
                  icon={<AddIcon />}
                  label="Create Trip"
                  onClick={() => navigate("/createtrip")}
                  position="end"
                  sx={{
                    backgroundColor: COLORS.primary,
                    color: 'white',
                    borderRadius:'12px',
                    '&:hover': {
                        backgroundColor: COLORS.burntOrange,
                    }
                  }}
                />
              </>
            </OptimizedTabPanel>

            {/* Bookings Tab */}
            <OptimizedTabPanel value={activeTabIndex} index={2}>
              <BookingsTab
                onTripClick={handleTripClick}
              />
            </OptimizedTabPanel>

            {/* Reservations Tab */}
            <OptimizedTabPanel value={activeTabIndex} index={3}>
              <ReservationsTab />
            </OptimizedTabPanel>

            {/* Transactions Tab */}
            <OptimizedTabPanel value={activeTabIndex} index={4}>
              <TransactionsTab />
            </OptimizedTabPanel>
          </>
        )}
      </Container>


      {/* Share Modal */}
      <ShareModal
        shareUrl={selectedTripForShare?.shareUrl || ''}
        open={shareModalOpen}
        onClose={handleShareModalClose}
        title={selectedTripForShare?.title || ''}
      />
    </Box>
  );
};

export default Trips;
